import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import type { ReasonModel } from '@/domain/models/reason.js';
import { useApp } from '@/presentation/hooks/use-app.js';

interface Props {
    open: boolean;
    title: string;
    reasons: ReasonModel[];
    reasonLabel: string;
    notes?: { label: string; placeholder: string; rows: number; required: boolean };
    confirm: { label: string; pendingLabel: string; color?: 'error' | 'warning' };
    onConfirm: (payload: { reasonId: string; notes: string }) => Promise<boolean>;
    onClose: () => void;
}

export function ReasonPromptDialog({ open, title, reasons, reasonLabel, notes, confirm, onConfirm, onClose }: Props) {
    const { translate } = useApp();
    const [reasonId, setReasonId] = useState('');
    const [draftNotes, setDraftNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (open) {
            setReasonId('');
            setDraftNotes('');
            setHasError(false);
        }
    }, [open]);

    const notesInvalid = !!notes?.required && !draftNotes.trim();
    const canSubmit = !!reasonId && !notesInvalid;

    const handleConfirm = async () => {
        if (!canSubmit) {
            return;
        }
        setHasError(false);
        setSubmitting(true);
        try {
            const ok = await onConfirm({ reasonId, notes: draftNotes.trim() });
            if (ok) {
                onClose();
                setReasonId('');
                setDraftNotes('');
            } else {
                setHasError(true);
            }
        } catch {
            setHasError(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth disableEscapeKeyDown={submitting} data-testid="reason-prompt-dialog">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField select label={reasonLabel} value={reasonId} onChange={(e) => setReasonId(e.target.value)} size="small" fullWidth data-testid="reason-prompt-dialog-reason">
                        {reasons.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                                {r.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {notes && (
                        <TextField
                            label={notes.label}
                            placeholder={notes.placeholder}
                            value={draftNotes}
                            onChange={(e) => setDraftNotes(e.target.value)}
                            size="small"
                            multiline
                            rows={notes.rows}
                            fullWidth
                            required={notes.required}
                            data-testid="reason-prompt-dialog-notes"
                        />
                    )}
                    {hasError && (
                        <Typography variant="caption" color="error" data-testid="reason-prompt-dialog-error">
                            {translate('errors.generic')}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} data-testid="reason-prompt-dialog-cancel">
                    {translate('common.cancel')}
                </Button>
                <Button
                    onClick={() => handleConfirm()}
                    color={confirm.color}
                    variant="contained"
                    sx={confirm.color ? undefined : { color: 'white' }}
                    disabled={submitting || !canSubmit}
                    data-testid="reason-prompt-dialog-confirm"
                >
                    {submitting ? confirm.pendingLabel : confirm.label}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
